import CustomTooltip from '../../../shared/CustomTooltip';
import { CalendarMonth, InfoOutlined, NavigateNext } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Grid,
    MenuItem,
    Paper,
    Select,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAllReservationsDetailedQuery } from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { BookingType, Status } from '../../../enums/booking';
import { Period } from '../../../enums/reports';
import SkeletonLayout from './SkeletonLayout';

type ActivityCardProps = {
    period?: Period;
    onPeriodChange?: (period: Period) => void;
};

const ActivityCard = ({
    period: controlledPeriod,
    onPeriodChange,
}: ActivityCardProps) => {
    const loggedInUser = UserRole();
    const [internalPeriod, setInternalPeriod] = useState(Period.CurrentWeek);
    const period = controlledPeriod ?? internalPeriod;
    const setPeriod = onPeriodChange ?? setInternalPeriod;
    const theme = useTheme();

    const { data: scheduledReservations } = useGetAllReservationsDetailedQuery({
        startDate: period,
        status: [Status.Approved, Status.Completed],
        bookingType: loggedInUser?.role.includes('owner')
            ? [BookingType.Guest, BookingType.OnBehalf]
            : undefined,
    });

    const { data: checkoutReservations } = useGetAllReservationsDetailedQuery({
        endDate: period,
        status: [Status.Approved, Status.Completed],
        bookingType: loggedInUser?.role.includes('owner')
            ? [BookingType.Guest, BookingType.OnBehalf]
            : undefined,
    });

    const { data: activeReservations } = useGetAllReservationsDetailedQuery({
        activeDate: period,
        status: [Status.Approved, Status.Completed],
        bookingType: loggedInUser?.role.includes('owner')
            ? [BookingType.Guest, BookingType.OnBehalf]
            : undefined,
    });

    const navigate = useNavigate();

    return (
        <Paper elevation={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='subHeading' sx={{ lineHeight: 2 }}>
                    Reservation Activity
                </Typography>
                <Select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value as Period)}
                    size='small'
                    sx={{ fontSize: '14px' }}
                >
                    <MenuItem value={Period.Today}>Today</MenuItem>
                    <MenuItem value={Period.Tomorrow}>Tomorrow</MenuItem>
                    <MenuItem value={Period.CurrentWeek}>This Week</MenuItem>
                    <MenuItem value={Period.PreviousWeek}>Last Week</MenuItem>
                    <MenuItem value={Period.NextWeek}>Next Week</MenuItem>
                    <MenuItem value={Period.CurrentMonth}>This Month</MenuItem>
                    <MenuItem value={Period.CurrentYear}>This Year</MenuItem>
                </Select>
            </Box>
            <Grid container sx={{ pt: 1 }} spacing={2}>
                <Grid item xs>
                    <Card
                        // variant='outlined'
                        sx={{
                            cursor: 'pointer',
                            transition: '0.3s ease-in-out',
                            height: '100%',
                            // bgcolor: dashboardItem.color,
                            // color: 'white',
                        }}
                    >
                        <CardContent
                            sx={{ m: 1 }}
                            onClick={() =>
                                navigate(`/dashboard/scheduled/${period}`)
                            }
                        >
                            {scheduledReservations ? (
                                <div>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                        }}
                                    >
                                        <Avatar
                                            variant='rounded'
                                            sx={{
                                                height: 25,
                                                width: 25,
                                                bgcolor:
                                                    theme.palette.neutral.main,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <CalendarMonth fontSize='small' />
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            Check-in
                                        </Typography>
                                        <NavigateNext />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '3rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {scheduledReservations?.length ?? 0}
                                    </Typography>
                                </div>
                            ) : (
                                <SkeletonLayout />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs>
                    <Card
                        // variant='outlined'
                        sx={{
                            cursor: 'pointer',
                            transition: '0.3s ease-in-out',
                            height: '100%',
                            // bgcolor: dashboardItem.color,
                            // color: 'white',
                        }}
                    >
                        <CardContent
                            onClick={() =>
                                navigate(`/dashboard/checkout?period=${period}`)
                            }
                            sx={{ m: 1 }}
                        >
                            {checkoutReservations ? (
                                <div>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                        }}
                                    >
                                        <Avatar
                                            variant='rounded'
                                            sx={{
                                                height: 25,
                                                width: 25,
                                                bgcolor:
                                                    theme.palette.neutral.main,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <CalendarMonth fontSize='small' />
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            Checkout
                                        </Typography>
                                        <NavigateNext />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '3rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {checkoutReservations.length}
                                    </Typography>
                                </div>
                            ) : (
                                <SkeletonLayout />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs>
                    <Card
                        // variant='outlined'
                        sx={{
                            cursor: 'pointer',
                            transition: '0.3s ease-in-out',
                            height: '100%',
                            // bgcolor: dashboardItem.color,
                            // color: 'white',
                        }}
                    >
                        <CardContent
                            onClick={() =>
                                navigate(
                                    `/dashboard/reservations?period=${period}`,
                                )
                            }
                            sx={{ m: 1 }}
                        >
                            {activeReservations ? (
                                <div>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Avatar
                                            variant='rounded'
                                            sx={{
                                                height: 25,
                                                width: 25,
                                                bgcolor:
                                                    theme.palette.neutral.main,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <CalendarMonth fontSize='small' />
                                        </Avatar>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Active Reservations
                                        </Typography>
                                        <CustomTooltip
                                            title={
                                                'Shows all approved reservations active during the selected period — including check-ins, checkouts, and ongoing stays.'
                                            }
                                        >
                                            <InfoOutlined
                                                sx={{ color: 'text.primary' }}
                                                fontSize='small'
                                            />
                                        </CustomTooltip>
                                        <NavigateNext />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '3rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {activeReservations.length}
                                    </Typography>
                                </div>
                            ) : (
                                <SkeletonLayout />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ActivityCard;
